/**
 * Desktop AuthClient (apps/desktop/INTEGRATION.md §7): sign-in happens in the system browser and Rust keeps the
 * session in the OS credential store. This side only asks Rust for 15-minute access tokens; it never sees or stores
 * the session token. The email-code and Google methods aren't offered here (the browser page has them).
 */
import { ApiFailure, type AuthClient, type BrowserSignInError, type BrowserSignInStatus } from "@bundle/core";
import {
  desktopAccessToken,
  desktopAuthStatus,
  desktopCompleteSignIn,
  desktopSignOut,
  desktopStartSignIn,
  onDesktopAuthChanged,
  type DesktopAuthErrorCode,
  type DesktopAuthStatus,
} from "./desktop.ts";

function browserError(code: DesktopAuthErrorCode): BrowserSignInError {
  switch (code) {
    case "cancelled":
      return "cancelled";
    case "no_pending_sign_in":
    case "sign_in_expired":
    case "state_mismatch":
      return "expired";
    case "invalid_grant":
      return "codeRejected";
    case "invalid_code":
      return "invalidCode";
    case "network":
      return "network";
    case "browser":
      return "browser";
    case "signed_out":
    case "server":
    case "internal":
      return "failed";
  }
}

/** A rejected auth command as an ApiFailure: `kind` for the session logic, `code` a BrowserSignInError. */
function failure(e: unknown): ApiFailure {
  const err = typeof e === "object" && e !== null ? (e as { code?: unknown; message?: unknown }) : {};
  const message = typeof err.message === "string" ? err.message : String(e);
  switch (err.code) {
    case "signed_out":
      return new ApiFailure("auth", 401, "signed_out", message);
    case "network":
      return new ApiFailure("network", 0, "network", message);
    case "server":
    case "internal":
      return new ApiFailure("server", 500, "failed", message);
    case "no_pending_sign_in":
    case "sign_in_expired":
    case "state_mismatch":
    case "invalid_code":
    case "cancelled":
    case "invalid_grant":
    case "browser":
      return new ApiFailure("invalid", 400, browserError(err.code), message);
    default:
      // Not one of ours (e.g. the IPC call isn't allowed): treat it like a server fault, never as signed out.
      return new ApiFailure("server", 500, "failed", message);
  }
}

async function call<T>(run: () => Promise<T>): Promise<T> {
  try {
    return await run();
  } catch (e) {
    throw failure(e);
  }
}

function toStatus(s: DesktopAuthStatus): BrowserSignInStatus {
  const status: BrowserSignInStatus = { signedIn: s.signedIn, signingIn: s.signingIn };
  if (s.userId !== null) status.userId = s.userId;
  if (s.error !== undefined) status.error = browserError(s.error);
  return status;
}

const browserOnly = () => Promise.reject(new ApiFailure("invalid", 400, "browser_sign_in", "Sign in with your browser"));

export function createDesktopAuth(): AuthClient {
  // The last token handed out. The session asks again only when it needs a newer one (expiring, or the API said
  // 401), so getting the same token back from Rust's cache means it's time to force a refresh.
  let last: string | undefined;
  return {
    sendEmailCode: browserOnly,
    verifyEmailCode: browserOnly,
    signInWithGoogle: browserOnly,
    async accessToken() {
      let t = await call(() => desktopAccessToken());
      if (t.token === last) t = await call(() => desktopAccessToken(true));
      last = t.token;
      return t.token;
    },
    async signOut() {
      last = undefined;
      await call(() => desktopSignOut());
    },
    browser: {
      status: async () => toStatus(await call(() => desktopAuthStatus())),
      start: () => call(() => desktopStartSignIn()),
      async complete(code) {
        await call(() => desktopCompleteSignIn(code));
      },
      subscribe(listener) {
        let off: (() => void) | undefined;
        let closed = false;
        void onDesktopAuthChanged((s) => listener(toStatus(s))).then((unlisten) => {
          if (closed) unlisten();
          else off = unlisten;
        });
        return () => {
          closed = true;
          off?.();
        };
      },
    },
  };
}
